/**
 * GridPage component module. Grid layout based on https://github.com/STRML/react-grid-layout
 * @module shared/components/GridPage
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import {Responsive, WidthProvider} from 'react-grid-layout';
import debug from 'debug';
import Icon from './Icon';
import {getBlackWhiteWithOpacity} from '../utils/colors';
import {PORTAL_NAME} from '../../config';

import './pages.css';
import '../../shared/reactGridLayout.css';

const log = debug('circleblack:components:GridPage');

// eslint-disable-next-line new-cap
const ResponsiveGridLayout = WidthProvider(Responsive);

const _PageBase = styled.div`
  background-image: url(${(props) => props.backgroundImage});
  background-size: cover;
  display: flex;
  flex-direction: column;
  max-width: 100vw;
  margin-right: ${props => {
    if (props.customPadding && PORTAL_NAME === 'investor') {
      return '0';
    }
    return (props.breakpoint >= 1700) ? '10px' : '0';
  }};
  //padding: 10px;

  .react-grid-item .react-resizable-handle::after {
    content: "";
    position: absolute;
    right: 3px;
    bottom: 3px;
    width: 5px;
    height: 5px;
    border-right: 2px solid rgba(255, 0, 0, 1);
    border-bottom: 2px solid rgba(255, 0, 0, 1);
  }

  .react-grid-item .react-resizable-handle.react-resizable-handle-se {
    bottom: 0;
    right: 0;
    cursor: se-resize;
  }

  .react-grid-item .react-resizable-handle {
    position: absolute;
    width: 20px;
    height: 20px;
  }
`;

const breakpoints = {
  xl: 1820,
  lg: 1090,
  md: 670,
  sm: 400,
  xs: 0,
};

const margin = [0, 0];
const padding = [5, 5];

/**
 * GridPage component to serve as container for all other components/modules.
 * @name GridPage
 */
const GridPage = props => {
  const {
    title,
    backgroundImage,
    cols = {xl: 1, lg: 1, md: 1, sm: 1, xs: 1},
    rowHeight = 850,
    onLayoutChange,
    onBreakpointChange,
    layouts,
    editing,
    children,
    customPadding,
    customMargin,
  } = props;

  return <_PageBase backgroundImage={backgroundImage} breakpoint={window?.innerWidth} customPadding={customPadding}>
    {title && <h1 className='page-title'>{title}</h1>}
    <ResponsiveGridLayout
      key={editing}
      className='layout'
      style={{margin: customMargin}}
      resizeHandles={editing ? ['se'] : []}
      breakpoints={breakpoints}
      cols={cols}
      rowHeight={rowHeight}
      isResizeable={editing}
      measureBeforeMount={false}
      useCSSTransforms={false}
      compactType={'vertical'}
      preventCollision={false}
      isBounded={true}
      isDraggable={editing}
      layouts={layouts}
      margin={margin}
      containerPadding={customPadding || padding}
      draggableCancel={'.cb-gridtile-hide, .cb-gridtile-resize'}
      resizeHandle={React.useMemo(() => (
        <div>
          {/* outer div is necessary for react-grid-layout to make this resizable */}
          <Icon
            className='cb-gridtile-resize'
            svg='tileResize'
            width='40px'
            height='40px'
            color={getBlackWhiteWithOpacity(0.7)}
          />
        </div>
      ), [])}
      onBreakpointChange={onBreakpointChange}
      onLayoutChange={(currentLayout, allLayouts) => {
        log('onLayoutChange changed', currentLayout, allLayouts);

        // ETODO Maybe Optional Callback
        // ETODO Look to improve this, it's to fix an issue where the scrollbar overlaps the right edge when resizing a tile that causes it to add scrollbar.
        window.dispatchEvent(new Event('resize'));
        if (onLayoutChange) {
          onLayoutChange(currentLayout, allLayouts);
        }
      }}>
      {children}
    </ResponsiveGridLayout>
  </_PageBase>;
};

export default GridPage;

GridPage.propTypes = {
  /**
   * If true uses grid system
   */
  grid: PropTypes.bool,
  /**
   * Number of columns for the page grid, if specified all must be specified.
   */
  cols: PropTypes.shape({
    /**
     * Xtra Large defaults to: ETODO
     */
    xl: PropTypes.number.isRequired,
    /**
     * Large defaults to: ETODO
     */
    lg: PropTypes.number.isRequired,
    /**
     * Medium defaults to: ETODO
     */
    md: PropTypes.number.isRequired,
    /**
     * Small defaults to: ETODO
     */
    sm: PropTypes.number.isRequired,
    /**
     * Xtra Small defaults to: ETODO
     */
    xs: PropTypes.number.isRequired,
  }),
  /**
   * Height in pixels of each grid row. Defaults to ETODO
   */
  rowHeight: PropTypes.number,
  /**
   * If true the layout can be edited
   */
  editable: PropTypes.bool,
  /**
   * Called whenever the layout changes, would be used to save layouts. https://github.com/STRML/react-grid-layout#responsive-grid-layout-props
   */
  onLayoutChange: PropTypes.func,
  /**
   * Called when screen is resized and breakpoints for grid change. https://github.com/STRML/react-grid-layout#responsive-grid-layout-props
   */
  onBreakpointChange: PropTypes.func,
  layouts: PropTypes.shape({
    /**
     * The layout for the grid. All numbers are 'Grid Units'. A "Grid Unit" is 1 Page Column for width and Page Row Height.
     */
    lg: PropTypes.arrayOf(PropTypes.shape(
      {
        /**
         * The Key of the Tile
         */
        i: PropTypes.string.isRequired,
        /**
         * The X (Horizontal) Position in the Grid
         */
        x: PropTypes.number.isRequired,
        /**
         * The Y (Vertical) Position in the Grid
         */
        y: PropTypes.number.isRequired,
        /**
         * The Width in Grid Units of the Tile. If greater then the Width of the Grid will be set to the Width of the Grid.
         */
        w: PropTypes.number.isRequired,
        /**
         * The Height in Grid Units of the Tile.
         */
        h: PropTypes.number.isRequired,
        /**
         * The Minimum Width of the Tile in Grid Units. If less then the Width of the Grid will be set to the Width of the Grid.
         */
        minW: PropTypes.number,
        /**
         * The Maximum Width of the Tile in Grid Units
         */
        maxW: PropTypes.number,
        /**
         * The Minimum Height of the Tile in Grid Units.
         */
        minH: PropTypes.number,
        /**
         * The Maximum Height of the Tile in Grid Units
         */
        maxH: PropTypes.number,
      }
    )),
  }),
  /**
   * To call when saving layouts
   */
  saveLayout: PropTypes.func,
  /**
   * The Title of the Page
   */
  title: PropTypes.string,
  /**
   * Tiles on the page
   */
  children: PropTypes.node,
  /**
   * Background Images for the page?? TODO REQs Do we want pages to support background images
   */
  backgroundImage: PropTypes.string,
  /**
   * UUID For editing or false for not
   */
  editing: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /**
   * Custom Padding for outer container
   */
  customPadding: PropTypes.arrayOf(PropTypes.number),
  /**
   * Custom Margin for grid page container
   */
  customMargin: PropTypes.string,
};
